<template>
  <div>
    <b-modal v-model="showDownloadModal"
             size="lg"
             :title="downloadAssetsTitle"
             hide-footer>
      <div class="p-3 text-center" v-if="!downloadLink">
        <div class="fa fa-spinner fa-spin fa-lg"></div>
        <div class="mt-3">
          {{ downloadModalText }}
        </div>
      </div>
      <div class="text-center p-3" v-else>
        Your download link is ready. <a :href="downloadLink" class="btn btn-link p-0 m-0">Click here</a> to download
      </div>
    </b-modal>
    <b-modal v-model="showPurchaseModal"
             size="lg"
             hide-footer
             :title-html="modalTitle">
      <BulkSubscriptionPurchase :has-purchase-requests="enabledActions.hasPurchaseRequests"
                                :selected-assets="selectedAssets"
                                @finished="handleAssetsPurchaseFinished"
                                @title="modalTitle = arguments[0]"
                                @cancel="showPurchaseModal = false"/>
    </b-modal>
    <bulk-selection-bar :items="assets"
                        class="mb-3"
                        v-if="hasBulkActions"
                        v-model="assetSelection"
                        :bulk-action-running="bulkActionRunning">
      <template v-slot:menuSlot>
        <b-button variant="outline"
                  v-if="enabledActions.buyer"
                  @click="showPurchaseModal = true">
          Purchase
        </b-button>
        <b-button variant="outline"
                  v-if="enabledActions.bulkRemoveFromProject"
                  @click="handleRemoveAssetsFromProject">
          Remove From This Project
        </b-button>

        <b-button variant="outline"
                  @click="handleBulkDownload"
                  :disabled="bulkDownloading"
                  v-if="enabledActions.bulkDownload">
          {{ downloadAssetsTitle }}
        </b-button>

        <b-dropdown variant="outline"
                    right
                    menu-class="dropdown-menu-projects"
                    text="Add to Project"
                    @shown="$refs.projectsDropdown.fetchProjects()"
                    :disabled="bulkActionRunning">
          <projects-dropdown v-if="enabledActions.bulkAddToProject"
                             :bulk-action-running="bulkActionRunning"
                             :url="projectsUrl"
                             ref="projectsDropdown"
                             @addToProject="handleAddAssetsToProject"/>
        </b-dropdown>

      </template>
    </bulk-selection-bar>

    <div class="card">
      <div class="card-body">
        <modal-navigation :assets="assets"
                          :tag-category-priority="tagCategoryPriority"
                          ref="modalNavigation"/>

        <b-row v-if="listType === 'large_cards'" cols-lg="3" cols-md="2" cols="1" no-gutters
               class="align-items-stretch">
          <b-col v-for="asset in assets"
                 class="p-1 asset-avatar-select-overlay d-flex flex-column tw-group"
                 :key="assetKey(asset)">
            <div
              class="img-bg text-white asset-square-item-image bottom-actions-hover-trigger asset-avatar-overlay-container"
              :style="backgroundImage(asset)">
              <a :href="assetPublicUrl(asset)" class="position-absolute w-100 h-100"></a>
              <video-hover-player v-if="asset.asset_type === 'Video'"
                                  :asset-id="asset.id"
                                  :thumb="asset.thumb_url"
                                  :video="asset.media_thumb_url"/>

              <div class="asset-avatar-overlay p-3 d-flex flex-column justify-content-end align-items-start">
                <span v-if="isComp(asset)" class="badge badge-pill badge-warning font-weight-semi-bold font-size-sm">UNWATERMARKED COMP</span>
              </div>

              <div @click.prevent.stop="handleAssetSelection(asset)"
                   class="image-select-control"
                   v-if="hasBulkActions"
                   :class="{ 'selection-area-expanded': selectedAssetsCount > 0 }">
                <b-form-checkbox v-model="assetSelection[asset.id]" @click.stop/>
              </div>

              <div class="d-flex bottom-actions align-items-center justify-content-end">
                <AssetCardBottom :asset="asset"
                                 :projectsUrl="projectsUrl"
                                 :projectAssetsUrl="projectAssetsUrl"
                                 :availableVersions="availableVersions(asset)"
                                 :assetDownloadRoute="assetDownloadRoute"
                                 :readOnlyMode="enabledActions.readOnlyMode"
                                 @preview="handleAssetClick(asset)"
                />
              </div>
            </div>
            <div class="d-flex justify-content-between align-items-center mt-1 flex-grow-1">
              <a class="font-weight-semi-bold text-dark" :href="assetPublicUrl(asset)">
                {{ asset.name }}
              </a>

              <template v-if="!enabledActions.readOnlyMode">
                <AssetBadgeAction :asset="asset" :availableVersions="availableVersions(asset)"/>
              </template>

            </div>
          </b-col>
        </b-row>

        <MasonryWall v-else :items="assets" :gap="20" :max-columns="3">
          <template #default="{ item: asset, index }">
            <MasonryAssetView class="tw-text-white tw-group tw-rounded-lg"
                              image-class="tw-rounded-lg"
                              :asset-url="assetPublicUrl(asset)"
                              :image-url="asset.watermark_medium_url">

              <video-hover-player v-if="asset.asset_type === 'Video'"
                                  :asset-id="asset.id"
                                  class="tw-rounded-lg"
                                  :thumb="asset.watermark_medium_url"
                                  :video="asset.media_thumb_url"/>

              <template #bottom-left>
                <AssetCardBottom :asset="asset"
                                 :projectsUrl="projectsUrl"
                                 :projectAssetsUrl="projectAssetsUrl"
                                 :availableVersions="availableVersions(asset)"
                                 :assetDownloadRoute="assetDownloadRoute"
                                 :readOnlyMode="enabledActions.readOnlyMode"
                                 @preview="handleAssetClick(asset)"/>
              </template>

              <template #top-right>
                <div class="tw-flex tw-flex-col tw-gap-2 tw-p-4 ">
                  <div @click.prevent.stop="handleAssetSelection(asset)"
                       class="image-select-control tw-pointer-events-auto"
                       v-if="hasBulkActions"
                       :class="{ 'selection-area-expanded': selectedAssetsCount > 0 }">
                    <b-form-checkbox v-model="assetSelection[asset.id]" @click.stop/>
                  </div>

                  <div v-if="isComp(asset)"
                       class="tw-self-start badge badge-pill badge-warning font-weight-semi-bold font-size-sm tw-invisible group-hover:tw-visible">
                    UNWATERMARKED COMP
                  </div>

                  <div class="text-shadow-sm tw-line-clamp-2 tw-invisible group-hover:tw-visible">{{ asset.name }}</div>
                </div>
              </template>
            </MasonryAssetView>
          </template>

        </MasonryWall>
      </div>
    </div>
  </div>
</template>

<script>
import GeneralMixins from '../../mixins/GeneralMixins'
import normalize from "json-api-normalize";
import Attributes from "../../models/Attributes";
import ModalNavigation from "../Common/ModalNavigation"
import BulkSelectionBar from "../Common/BulkSelectionBar";
import ProjectsDropdown from "./ProjectsDropdown";
import AddToProject from "../Customers/Dashboard/AddToProject";
import DownloadsDropdown from "./DownloadsDropdown";
import BulkSubscriptionPurchase from "../Customers/BulkPurchase/BulkSubscriptionPurchase.vue";
import consumer from "../../../channels/consumer.js";
import MasonryAssetView from "./MasonryAssetView.vue";
import AssetCardBottom from "./AssetCardBottom.vue";
import AssetBadgeAction from "./AssetBadgeAction.vue";
import MasonryWall from "../Common/MasonryWall.vue";

export default {
  props: [
    'assets',
    'listType',
    'tagCategoryPriority',
    'originalFavorites',
    'projectsUrl',
    'projectAssetsUrl',
    'enabledActions',
    'project',
    'licenses',
    'assetDownloadRoute'
  ],

  data() {
    return {
      favorites: this.originalFavorites,
      assetSelection: {},
      bulkActionRunning: false,
      showPurchaseModal: false,
      showDownloadModal: false,
      subscribedToChannel: false,
      downloadLink: null,
      bulkDownloading: false,
      downloadModalText: "",
      currentLicenses: this.licenses,
      modalTitle: "Bulk License Purchase"
    }
  },

  mounted() {
    // this.bootup()
  },

  methods: {


    bootup() {
      const selection = {};
      this.assets.forEach(asset => selection[asset.id] = true);
      this.assetSelection = selection
      this.showPurchaseModal = true
    },

    handleAssetsPurchaseFinished(licenses) {
      this.currentLicenses = [...this.currentLicenses, ...licenses]
      this.assetSelection = {}
      this.showPurchaseModal = false
    },

    assetKey(asset) {
      const idx = this.assets.indexOf(asset);
      return `${asset.id}-${idx}-${asset.updated_at}`
    },

    favoriteIconClass(asset) {
      return this.favoriteForAsset(asset) ? 'fas' : 'far';
    },

    favoriteForAsset(asset) {
      return this.favorites.find(fav => {
        return fav.favorable_id === asset.id && fav.favorable_type === asset.asset_type
      })
    },

    handleFavorite(asset) {
      const fav = this.favoriteForAsset(asset);
      if (fav) {
        this.$http.delete(`/favorites/${fav.id}`).then(fulfill => {
          const idx = this.favorites.indexOf(fav);
          if (idx > -1) {
            this.favorites.splice(this.favorites.indexOf(fav), 1);
          }
        }, reject => {
          this.showErrorToast("There was an error while removing from favorites. Please try again later")
        })
      } else {
        this.$http.post('/favorites', {
          favorable_id: asset.id,
          favorable_type: asset.asset_type
        }).then(fulfill => {
          this.favorites.push(normalize(fulfill.body).get(Attributes.favoriteAttributes));
        }, reject => {
          this.showErrorToast("There was an error while adding to favorites. Please try again later")
        });
      }

    },

    backgroundImage(asset) {
      return `background-image: url(${asset.thumb_url})`
    },

    handleAssetClick(asset) {
      this.$refs.modalNavigation.modalAsset = asset;
    },

    handleAssetSelection(file) {
      this.$set(this.assetSelection, file.id, !this.assetSelection[file.id]);
    },

    handleAddAssetsToProject(project) {
      this.$http.patch(`${this.projectAssetsUrl}/${project.id}`, {
        project: {
          added_asset_ids: this.selectedAssetIds
        }
      }).then(fulfilled => {
        const count = this.selectedAssetIds.length;
        const project = normalize(fulfilled.body).get(Attributes.projectAttributes);
        window.projectsStore.updateProject(project);

        const h = this.$createElement;
        const toastMessage = h('div', [
          `${count} ${count === 1 ? 'asset was' : 'assets were'} added to `,
          h(
            'a',
            {attrs: {href: `${this.projectsUrl}/${project.id}`}},
            [project.name]
          )
        ])

        this.showToast(toastMessage, "Success");
        this.closeSelection();
      }, rejected => {
        this.showErrorToast("There was an error while adding these assets");
      })
    },

    handleRemoveAssetsFromProject() {
      if (confirm("Are you sure you want to remove the selected assets from this project?")) {
        const remainingIds = this.assets.filter(asset => !this.selectedAssetIds.includes(asset.id)).map(asset => asset.id);
        this.$http.patch(`${this.projectsUrl}/${this.project.id}`, {
          project: {
            asset_ids: remainingIds
          }
        }).then(fulfill => {
          location.reload();
        }, rejected => {
          this.showErrorToast("There was an error while removing these assets")
        })
      }
    },

    closeSelection() {
      this.assetSelection = {};
    },

    availableVersions(asset) {
      if (this.currentLicenses && this.currentLicenses.length > 0) {
        return this.currentLicenses.filter(license => license.asset_id === asset.id).map(license => license.version_size);
      }

      return [];
    },

    isComp(asset) {
      if (this.currentLicenses && this.currentLicenses.length > 0) {
        return this.currentLicenses.find(license => license.asset_id === asset.id && license.version_size === 'comp')
      }

      return false
    },

    handleBulkDownload() {
      this.bulkDownloading = true
      this.downloadLink = null
      this.downloadModalText = ''
      this.showDownloadModal = true

      this.subscribeToSocket()

      this.$http.post("/customers/bulk/downloads", {
        license_ids: this.selectedLicenseIds
      }).then(fulfilled => {
        this.downloadModalText = fulfilled.data.message
        this.showDownloadModal = true
        this.bulkDownloading = false
      }, rejected => {
        this.showErrorToast(this.getErrorMessages(rejected.body, true));
        this.bulkDownloading = false
      })
    },

    subscribeToSocket() {
      if (this.subscribedToChannel) {
        return;
      }

      const vm = this

      consumer.subscriptions.create("CustomerChannel", {
        connected: () => {
          console.log("Connected to customer channel");
          vm.subscribedToChannel = true;
        },

        disconnected() {
          console.log("Disconnected from customer channel");
          vm.subscribedToChannel = false;
        },

        received(data) {
          if (data.url) {
            vm.downloadLink = data.url
          }
        }
      })
    },
  },

  computed: {
    downloadAssetsTitle() {
      return `Download ${this.selectedAssetsCount} ${this.selectedAssetsCount === 1 ? 'asset' : 'assets'}`
    },

    selectedLicenseIds() {
      return this.selectedAssets.map(asset => {
        return this.currentLicenses.find(license => license.asset_id === asset.id)
      }).filter(license => license).map(license => license.id)
    },

    hasBulkActions() {
      return this.enabledActions
        && Object.keys((this.enabledActions)).map(k => this.enabledActions[k]).filter(v => v === true).length > 0
        && !this.enabledActions.readOnlyMode;
    },

    selectedAssets() {
      if (this.selectedAssetIds.length > 0) {
        return this.selectedAssetIds.map(assetId => {
          return this.assets.find(asset => asset.id === assetId)
        })
      }
      return [];
    },

    selectedAssetsCount() {
      return this.selectedAssets.length;
    },

    selectedAssetIds: function () {
      return Object.keys(this.assetSelection).filter(key => this.assetSelection[key] === true);
    },
  },

  mixins: [GeneralMixins],
  components: {
    MasonryWall,
    AssetBadgeAction,
    AssetCardBottom,
    MasonryAssetView,
    BulkSubscriptionPurchase,
    DownloadsDropdown, AddToProject, ProjectsDropdown, BulkSelectionBar, ModalNavigation
  }
}
</script>